import {
	Dialog,
	DialogTitle,
	DialogContent,
	Box,
	DialogActions,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	TextField,
	CircularProgress,
	Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
	getAvailableScripts,
	getAvailableScriptsLoading,
	getAssignScriptLoading,
} from 'redux/reducers/emAutomationScripts/selectors';
import {
	getAvailableScriptsRequest,
	assignScriptRequest,
} from 'redux/reducers/emAutomationScripts/reducer';
import { TEmAutomationAssignedScriptsRequest } from 'redux/reducers/emAutomationScripts/types';

export type TAddScriptDialogProps = {
	open: boolean;
	setClose: () => void;
	getAssignedScriptsRequest: TEmAutomationAssignedScriptsRequest;
};

export const AddScriptDialog = ({
	open,
	setClose,
	getAssignedScriptsRequest,
}: TAddScriptDialogProps) => {
	const { emId } = useParams();
	const dispatch = useDispatch();

	const availableScripts = useSelector(getAvailableScripts);
	const availableScriptsLoading = useSelector(getAvailableScriptsLoading);
	const assignScriptLoading = useSelector(getAssignScriptLoading);

	const [selectedScriptId, setSelectedScriptId] = useState<number | ''>('');
	const [ladybugName, setLadybugName] = useState('');
	const [ladybugPrice, setLadybugPrice] = useState<number | ''>('');
	const [ladybugCurrency, setLadybugCurrency] = useState('EUR');
	const [errors, setErrors] = useState<Record<string, string>>({});

	useEffect(() => {
		if (open) {
			dispatch(getAvailableScriptsRequest());
		}
	}, [dispatch, open]);

	const reset = () => {
		setSelectedScriptId('');
		setLadybugName('');
		setLadybugPrice('');
		setLadybugCurrency('EUR');
		setErrors({});
	};

	const validate = () => {
		const newErrors: Record<string, string> = {};

		if (!selectedScriptId) {
			newErrors.scriptId = 'Script is required';
		}
		// if (!ladybugName.trim()) {
		// 	newErrors.ladybugName = 'Product name is required';
		// }
		// if (ladybugPrice === '' || ladybugPrice < 0) {
		// 	newErrors.ladybugPrice = 'Product price is required and must be >= 0';
		// }
		if (!ladybugCurrency.trim()) {
			newErrors.ladybugCurrency = 'Currency is required';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleClose = () => {
		reset();
		setClose();
	};

	const handleAssign = () => {
		if (!validate() || !emId || !selectedScriptId) return;

		dispatch(
			assignScriptRequest({
				data: {
					emId: Number(emId),
					scriptId: selectedScriptId,
					ladybugName: ladybugName.trim(),
					ladybugPrice: Number(ladybugPrice),
					ladybugCurrency: ladybugCurrency.trim(),
				},
				getAssignedScriptsRequest,
			}),
		);
		handleClose();
	};

	const selectedScript = availableScripts?.find((s) => s.id === selectedScriptId);

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			sx={{
				'& .MuiDialog-paper': {
					width: '600px',
					maxWidth: '600px',
					borderRadius: '12px',
				},
			}}
		>
			<DialogTitle color="primary">Add Automation Script</DialogTitle>
			<DialogContent sx={{ p: '12px 12px 12px 12px' }}>
				<Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
					<FormControl fullWidth error={!!errors.scriptId}>
						<InputLabel id="script-select-label">Script</InputLabel>
						<Select
							labelId="script-select-label"
							value={selectedScriptId}
							label="Script"
							onChange={(e) => setSelectedScriptId(e.target.value as number)}
							disabled={availableScriptsLoading}
						>
							{availableScriptsLoading && (
								<MenuItem disabled>
									<CircularProgress size={20} />
								</MenuItem>
							)}
							{availableScripts?.map((script) => (
								<MenuItem key={script.id} value={script.id}>
									{script.shortDescription}
								</MenuItem>
							))}
						</Select>
						{errors.scriptId && (
							<Typography variant="caption" color="error">
								{errors.scriptId}
							</Typography>
						)}
					</FormControl>

					{selectedScript && (
						<Box sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
							<Typography variant="body2" color="text.secondary">
								<strong>Description:</strong> {selectedScript.description}
							</Typography>
						</Box>
					)}

					<TextField
						label="Product Name"
						value={ladybugName}
						onChange={(e) => setLadybugName(e.target.value)}
						fullWidth
					/>

					<TextField
						label="Product Price"
						type="number"
						value={ladybugPrice}
						onChange={(e) => setLadybugPrice(e.target.value === '' ? '' : Number(e.target.value))}
						fullWidth
						inputProps={{ min: 0, step: 0.01 }}
					/>

					<TextField
						label="Currency"
						value={ladybugCurrency}
						onChange={(e) => setLadybugCurrency(e.target.value)}
						fullWidth
						error={!!errors.ladybugCurrency}
						helperText={errors.ladybugCurrency}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>Cancel</Button>
				<Button
					onClick={handleAssign}
					color="primary"
					variant="contained"
					disabled={assignScriptLoading || !selectedScriptId}
				>
					{assignScriptLoading ? <CircularProgress size={20} /> : 'Add Script'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
